import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { UserRankingDto } from '@repo/shared/dtos/analytics/UtilizationDto';
import { AnalyticsModal } from '@repo/ui/components/Modal/AnalyticsModal';
import Pagination from '@repo/ui/components/Pagination';
import { Users, Trophy, Search } from 'lucide-react';
import { ExportButton } from '../../../../utils/export';
import { ExportFormat } from '../../../../utils/export/types';
import { useState, useMemo, useEffect } from 'react';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { useUserRankings } from '../../../../hooks/analytics/useUtilizationAnalytics';
import { useSession } from '../../../../hooks/auth/useSession';
import { FilterSummary } from '../shared/FilterSummary';
import { InsightKpiRow, avgSessionsPerMonth } from './InsightKpiRow';
import { isChampion, isPowerMember, isInactive } from '../utils/tierUtils';
import { MemberFilterTabs, type MemberFilter } from './MemberFilterTabs';
import { MemberTable } from './MemberTable';
import { useFilteredMembers, useMemberSearch, useMemberPagination } from '../hooks';
import {
  TOP_USERS_PAGE_SIZE,
  TOP_USERS_MODAL_LIMIT,
  TIER_LABEL_CHAMPION,
  TIER_LABEL_POWER,
  TIER_LABEL_REGULAR,
  TIER_LABEL_INACTIVE,
} from '../constants';

function SkeletonRow() {
  return (
    <tr className="border-b border-[var(--anl-border)]">
      <td className="px-3 py-3">
        <div className="mx-auto h-5 w-5 animate-pulse rounded bg-[var(--anl-surface-elev)]" />
      </td>
      <td className="px-3 py-3">
        <div className="h-4 w-32 animate-pulse rounded bg-[var(--anl-surface-elev)]" />
      </td>
      <td className="px-3 py-3">
        <div className="ml-auto h-4 w-10 animate-pulse rounded bg-[var(--anl-surface-elev)]" />
      </td>
      <td className="px-3 py-3">
        <div className="ml-auto h-4 w-12 animate-pulse rounded bg-[var(--anl-surface-elev)]" />
      </td>
      <td className="px-3 py-3">
        <div className="ml-auto h-4 w-8 animate-pulse rounded bg-[var(--anl-surface-elev)]" />
      </td>
      <td className="px-3 py-3">
        <div className="h-4 w-28 animate-pulse rounded bg-[var(--anl-surface-elev)]" />
      </td>
      <td className="px-3 py-3">
        <div className="mx-auto h-4 w-16 animate-pulse rounded bg-[var(--anl-surface-elev)]" />
      </td>
    </tr>
  );
}

function TableSkeleton() {
  return (
    <div className="mb-4 overflow-x-auto rounded-lg border border-[var(--anl-border)]">
      <table className="w-full table-fixed divide-y divide-[var(--anl-border)]">
        <colgroup>
          <col className="w-14" />
          <col className="w-[22%]" />
          <col className="w-[11%]" />
          <col className="w-[11%]" />
          <col className="w-[13%]" />
          <col className="w-[27%]" />
          <col className="w-[16%]" />
        </colgroup>
        <thead className="bg-[var(--anl-surface-elev)]">
          <tr>
            {['#', 'Name', 'Sessions', 'Minutes', 'This Month', 'Most Used', 'Last Session'].map(
              label => (
                <th
                  key={label}
                  className="px-3 py-3 text-xs font-medium uppercase tracking-wider text-[var(--anl-text-muted)]"
                >
                  <div className="h-3 w-12 animate-pulse rounded bg-[var(--anl-border)]" />
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--anl-border)] bg-[var(--anl-surface)]">
          {Array.from({ length: TOP_USERS_PAGE_SIZE }).map((_, i) => (
            <SkeletonRow key={i} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface TopUsersModalProps {
  data: UserRankingDto[];
  isOpen: boolean;
  onClose: () => void;
  filters: BaseAnalyticsFilter;
  onItemClick?: (user: UserRankingDto) => void;
}

export function TopUsersModal({
  data: _initialData,
  isOpen,
  onClose,
  filters,
  onItemClick,
}: TopUsersModalProps) {
  const isMobile = useIsMobile();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<MemberFilter>('all');
  const { session } = useSession();
  const isLumenaAdmin = !!session?.isglobal;

  const { data: allUsersData, isLoading } = useUserRankings(
    filters,
    'sessions',
    TOP_USERS_MODAL_LIMIT,
  );

  const { allUsers, champions, powerUsers, atRiskUsers, filteredByTab } = useFilteredMembers(
    allUsersData ?? [],
    activeFilter,
  );

  const filteredData = useMemberSearch(filteredByTab, searchTerm);

  const { paginatedData, totalPages, startRank } = useMemberPagination(filteredData, currentPage);

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleFilterChange = (tab: MemberFilter) => {
    if (!isLumenaAdmin && (tab === 'power' || tab === 'inactive')) {
      setActiveFilter('all');
    } else {
      setActiveFilter(tab);
    }
    setCurrentPage(1);
    setSearchTerm('');
  };

  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setSearchTerm('');
      if (!isLumenaAdmin && (activeFilter === 'power' || activeFilter === 'inactive')) {
        setActiveFilter('all');
      }
    }
  }, [isOpen, isLumenaAdmin]);

  const exportData = useMemo(
    () =>
      filteredData.map((user, index) => {
        const baseExportData = {
          rank: index + 1,
          firstName: user.first_name,
          lastName: user.last_name,
          fullName: `${user.first_name} ${user.last_name}`,
          userType: user.user_type,
          totalSessions: user.total_sessions,
          totalMinutes: Math.round(user.total_minutes),
          averageSessionDuration:
            user.total_sessions > 0 ? Math.round(user.total_minutes / user.total_sessions) : 0,
          sessionsThisMonth: user.sessions_this_month,
          minutesThisMonth: Math.round(user.minutes_this_month),
          mostUsedExperience: user.most_used_experience ?? 'N/A',
          firstVisitDate: user.first_visit_date,
          lastSessionDate: user.last_session_date,
          daysSinceLastSession: user.days_since_last_session,
        };

        if (!isLumenaAdmin) {
          return baseExportData;
        }

        let memberTier: string;
        if (isChampion(user)) memberTier = TIER_LABEL_CHAMPION;
        else if (isPowerMember(user)) memberTier = TIER_LABEL_POWER;
        else if (isInactive(user)) memberTier = TIER_LABEL_INACTIVE;
        else memberTier = TIER_LABEL_REGULAR;

        return {
          ...baseExportData,
          memberTier,
          avgSessionsPerMonth: avgSessionsPerMonth(user).toFixed(1),
        };
      }),
    [filteredData, isLumenaAdmin],
  );

  const headerContent = !isLoading && allUsers.length > 0 && (
    <ExportButton
      data={exportData}
      config={{ fileName: 'top-members', date: true }}
      format={ExportFormat.CSV}
      className="text-[var(--anl-text-muted)] transition-colors hover:text-[var(--anl-text)]"
    />
  );

  return (
    <AnalyticsModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <Users className="h-6 w-6" />
          Top Members
        </span>
      }
      headerContent={headerContent || undefined}
      className="max-w-7xl"
    >
      <div className={isMobile ? 'p-3' : 'p-6'}>
        <FilterSummary filters={filters} />

        {isLoading ? (
          <>
            {/* KPI row skeleton — 3 tiles */}
            <div className="mb-5 grid grid-cols-3 gap-2 sm:gap-3">
              {[0, 1, 2].map(i => (
                <div
                  key={i}
                  className="flex flex-col items-center gap-2 rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface-elev)] px-2 py-2 sm:px-4 sm:py-3"
                >
                  <div className="h-3 w-full max-w-[4rem] animate-pulse rounded bg-[var(--anl-border)]" />
                  <div className="h-7 w-10 animate-pulse rounded bg-[var(--anl-border)]" />
                  <div className="hidden h-3 w-full max-w-[5rem] animate-pulse rounded bg-[var(--anl-border)] sm:block" />
                </div>
              ))}
            </div>
            <div className="mb-4 flex flex-wrap gap-2">
              {[0, 1, 2, 3].map(i => (
                <div
                  key={i}
                  className="h-8 w-16 animate-pulse rounded-full bg-[var(--anl-surface-elev)] sm:w-24"
                />
              ))}
            </div>
            <div className="mb-4 h-10 w-full animate-pulse rounded-lg bg-[var(--anl-surface-elev)]" />
            <TableSkeleton />
          </>
        ) : !allUsers.length ? (
          <div className="flex flex-col items-center justify-center py-12 text-[var(--anl-text-muted)]">
            <Users className="mb-4 h-16 w-16 text-[var(--anl-border)]" />
            <h3 className="mb-2 text-lg font-medium text-[var(--anl-text)]">
              No Member Data Available
            </h3>
            <p className="text-center text-[var(--anl-text-muted)]">
              Member rankings will appear here once members complete sessions with the applied
              filters.
            </p>
          </div>
        ) : (
          <>
            {isLumenaAdmin && <InsightKpiRow data={allUsers} />}

            {isLumenaAdmin && (
              <MemberFilterTabs
                active={activeFilter}
                onChange={handleFilterChange}
                counts={{
                  all: allUsers.length,
                  champion: champions.length,
                  power: powerUsers.length,
                  atRisk: atRiskUsers.length,
                }}
                isLumenaAdmin={isLumenaAdmin}
              />
            )}

            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-[var(--anl-text-muted)]" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => handleSearchChange(e.target.value)}
                placeholder="Search members by name..."
                className="w-full rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface)] py-2 pl-10 pr-4 text-[var(--anl-text)] placeholder-[var(--anl-text-muted)] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--anl-primary)]"
              />
            </div>

            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className={`flex items-center space-x-2 font-bold text-[var(--anl-text)] ${isMobile ? 'text-base' : 'text-xl'}`}>
                  <Trophy className="h-5 w-5" style={{ color: 'var(--anl-warning)' }} />
                  <span>Member Rankings</span>
                </h2>
                <p className="mt-1 text-sm text-[var(--anl-text-muted)]">
                  {searchTerm
                    ? `Showing ${paginatedData.length} of ${filteredData.length} filtered members`
                    : `Showing ${paginatedData.length} of ${filteredByTab.length} members`}
                </p>
              </div>
            </div>

            <MemberTable
              data={paginatedData}
              onUserClick={onItemClick}
              startRank={startRank}
              isLumenaAdmin={isLumenaAdmin}
            />

            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              variant="dark"
            />

            {filteredData.length > TOP_USERS_PAGE_SIZE && (
              <p className="mt-2 text-center text-sm text-[var(--anl-text-muted)]">
                {filteredData.length} total members
              </p>
            )}

            {!searchTerm && allUsers.length > 10 && activeFilter === 'all' && (
              <div className="mt-6 border-t border-[var(--anl-border)] pt-4">
                <p className="text-center text-sm text-[var(--anl-text-muted)]">
                  {`${allUsers.length} members total • Click any member for detailed analysis`}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </AnalyticsModal>
  );
}
